import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import type { TabsProps } from 'antd';
import { get, isNil, isString } from 'lodash';
import { FieldArrayRenderProps, FormikProps } from 'formik';
import flatten from 'flat';
import { useDidUpdate } from 'rooks';

import {
  payRateTypesSelector,
  payCodesSelector,
  payCodesGroupsSelector,
  payCodesDetailsInfoSelector,
} from 'redux/selectors/assignmentSelectors';
import {
  FinancialInfoFormValues,
  MultiRateFormValues,
} from 'components/modals/AssignmentDataModal/FinancialInfo';
import FinancialSchedulerContext from 'components/FinancialSchedulerContext';
import { useAppSelector } from './useAppSelector';
import MultiRateDetails from 'components/modals/AssignmentDataModal/FinancialInfo/MultiRateDetails';
import {
  AssignmentTypeEnum,
  JobCategoryEnum,
  PayCodeModel,
  PayRateTypeModel,
} from 'api/backofficeApiClient';
import { FinancialSchedulerFormValues } from 'components/modals/FinancialInfoScheduleModal';
import {
  financialInfoTabId,
  ptoMultiRateId,
} from 'constants/MultiRateConstants';
import { FinancialInfoCalculator } from '../components/modals/AssignmentDataModal/FinancialInfo/calculationUtils';
import { filterPayTypes } from 'utils/multiRates/multiRatesUtils';
import { getFilteredPayCodesByJobCriteria } from 'features/assignment/AssignmentDetailsPage/utils';

export enum MultiRatesTypeEnum {
  Schedule = 1,
}

const getTabLabel = (name?: string, hasError?: boolean) => {
  return (
    <div className={hasError ? 'tab-name' : ''}>
      {name}
      {hasError && <i className="icon icon-error-outlined" />}
    </div>
  );
};

export const MULTI_RATES_KEY = 'multiRates';
const financialInfoLabel = 'Financial Info';

export type FinancialFormValues =
  | FinancialInfoFormValues
  | FinancialSchedulerFormValues;

export type FinancialInfoOptions = FieldArrayRenderProps & {
  renderFinancialInfoSection: ReactNode;
  mode?: MultiRatesTypeEnum;
  selectedAssignmentType: AssignmentTypeEnum | null;
  jobCategory?: JobCategoryEnum | null;
};

export default function useFinancialInfoTabs(props: FinancialInfoOptions) {
  const {
    form,
    push,
    remove,
    replace,
    renderFinancialInfoSection,
    mode,
    selectedAssignmentType,
    jobCategory,
  } = props;
  const { predefinedFinancialSchedulerValues } = useContext(
    FinancialSchedulerContext,
  );
  const payRateTypes = useAppSelector(payRateTypesSelector);
  const payCodesDetails = useAppSelector(payCodesSelector);
  const payCodesGroups = useAppSelector(payCodesGroupsSelector);
  const payCodesDetailsInfo = useAppSelector(payCodesDetailsInfoSelector);
  const [payCodes, setPayCodes] = useState<PayCodeModel[]>([]);
  const existingMultiRates: MultiRateFormValues[] =
    form.values[MULTI_RATES_KEY] || [];

  const updatePayCodes = useCallback(() => {

    const codes = getFilteredPayCodesByJobCriteria({
      payCodesDetails,
      assignmentType: selectedAssignmentType,
      jobCategory,
      payCodesGroups,
      payCodesDetailsInfo,
    });
    setPayCodes(codes);
  }, [
    selectedAssignmentType,
    payCodesDetails,
    jobCategory,
    payCodesGroups,
    payCodesDetailsInfo,
  ]);

  useEffect(() => {
    updatePayCodes();
  }, [updatePayCodes]);

  useDidUpdate(() => {
    const updatedTabs = initializeTabs();
    setTabs(updatedTabs);
    // eslint-disable-next-line
  }, [form.values?.isCanUpdateScheduler]);

  useDidUpdate(() => {
    const updatedTabs = initializeTabs();
    setTabs(updatedTabs);
    const activeTab = getInitialActiveTab();
    setActiveTab(activeTab);
  }, [payCodes, predefinedFinancialSchedulerValues?.multiRates]);

  const modifyPayType = (
    payTypeId: string,
    index: number,
    form: FormikProps<FinancialFormValues>,
  ) => {
    const newTab = payRateTypes.find((payType) => payType.id === payTypeId);

    if (!newTab?.id) return;

    changeActiveTab(payTypeId);

    const matchingPayCodes = filterMatchingPayCodes(payTypeId);
    const newValue = generateInitialValue(
      payTypeId,
      matchingPayCodes,
      form.values,
    );

    replace(index, newValue);

    setTabs((oldTabs) => {
      if (!oldTabs?.length) return [];

      return oldTabs.map((oldTab, oldTabIndex) =>
        oldTabIndex === index + 1
          ? {
              ...oldTab,
              key: newTab.id || '',
              label: getTabLabel(newTab.name),
            }
          : oldTab,
      );
    });
  };

  const createNewTab = (nextUnusedTab: PayRateTypeModel, index: number) => {
    const hasSchedulerChanges = Boolean(
      predefinedFinancialSchedulerValues?.multiRates?.find(
        (multiRate) => multiRate.payTypeId === nextUnusedTab.id,
      ),
    );

    return {
      key: nextUnusedTab.id || '',
      label: getTabLabel(nextUnusedTab.name),
      children: (
        <MultiRateDetails
          index={index}
          formNamePrefix={MULTI_RATES_KEY}
          updatePayType={modifyPayType}
          isScheduleMode={mode === MultiRatesTypeEnum.Schedule}
          assignmentType={selectedAssignmentType}
          payTypeId={nextUnusedTab.id || ''}
        />
      ),
      closable: !hasSchedulerChanges,
    };
  };

  const initializeTabs = () => {
    const isScheduler = mode === MultiRatesTypeEnum.Schedule;
    const items: TabsProps['items'] = [];
    if (
      !isScheduler ||
      (isScheduler && !isNil(form.values?.financialInfoScheduler))
    ) {
      items.push({
        label: getTabLabel(financialInfoLabel),
        key: financialInfoTabId,
        children: renderFinancialInfoSection,
        closable: false,
      });
    }

    const multiRatesFormValues: MultiRateFormValues[] =
      form.values[MULTI_RATES_KEY] || [];

    const multiRateTabs: TabsProps['items'] = multiRatesFormValues
      .map((multiRate, index) => {
        const payRateTypeInfo = payRateTypes.find(
          (payRateType) => payRateType.id === multiRate.payTypeId,
        );

        if (!payRateTypeInfo) return null;

        return createNewTab(payRateTypeInfo, index);
      })
      .filter((item) => item !== null) as TabsProps['items'];
    return [...items, ...(multiRateTabs || [])];
  };
  const [tabs, setTabs] = useState<TabsProps['items']>(initializeTabs);

  const getInitialActiveTab = () => {
    const isScheduler = mode === MultiRatesTypeEnum.Schedule;
    const existingMultiRates: MultiRateFormValues[] =
      form.values[MULTI_RATES_KEY] || [];

    if (!isScheduler) return financialInfoTabId;

    return !isNil(form.values?.financialInfoScheduler)
      ? financialInfoTabId
      : (existingMultiRates[0]?.id as string);
  };
  const [activeTab, setActiveTab] = useState(getInitialActiveTab);

  const changeActiveTab = (activeTab: string) => {
    setActiveTab(activeTab);
  };

  const addNewTab = () => {
    if (!(tabs && tabs.length)) return;

    const usedPayTypeIds = existingMultiRates.map(
      (multiRate) => multiRate.payTypeId,
    );

    const unusedPayTypes = filterPayTypes({
      payRateTypes,
      payCodes,
      usedPayTypeIds,
    });

    const nextUnusedTab = unusedPayTypes[0];

    if (!isNil(nextUnusedTab?.id)) {
      const matchingPayCodes = filterMatchingPayCodes(nextUnusedTab.id);
      const initialValue = generateInitialValue(
        nextUnusedTab.id,
        matchingPayCodes,
        form.values,
      );

      push(initialValue);
      changeActiveTab(nextUnusedTab.id);

      const newTab = createNewTab(nextUnusedTab, tabs.length - 1);

      setTabs([...tabs, newTab]);
    }
  };

  const removeTab = (
    selectedKey: React.MouseEvent | React.KeyboardEvent | string,
  ) => {
    if (!tabs?.length) return;

    const removedValueIndex = existingMultiRates.findIndex(
      (multiRate) => multiRate.payTypeId === selectedKey,
    );
    remove(removedValueIndex);

    const updatedTabs = tabs.filter((tab) => tab.key !== selectedKey);
    const updatedTabsWithChildren = refreshTabChildren(updatedTabs);

    if (selectedKey === activeTab) {
      const removedTabIndex = tabs.findIndex((tab) => tab.key === selectedKey);
      const previousTab = tabs[removedTabIndex - 1];
      changeActiveTab(previousTab.key);
    } else {
      changeActiveTab(activeTab);
    }

    setTabs(updatedTabsWithChildren);
  };

  const filterMatchingPayCodes = (payTypeId: string) => {
    return payCodes.filter((payCode) => payCode.payRateTypeId === payTypeId);
  };

  const generateInitialValue = (
    payTypeId: string,
    matchingPayCodes: PayCodeModel[],
    formValues?: FinancialInfoFormValues,
  ) => {
    return {
      payTypeId: payTypeId,
      activePayCodeId: matchingPayCodes?.[0]?.id || undefined,
      payCodesInfo: matchingPayCodes.length
        ? matchingPayCodes.map((payCode) => ({
            payCodeId: payCode.id,
            payRate:
              payTypeId === ptoMultiRateId
                ? FinancialInfoCalculator.calculatePayRatePTOValue(
                    formValues?.payRate,
                  )
                : undefined,
            billRate:
              payTypeId === ptoMultiRateId
                ? FinancialInfoCalculator.calculateBillRatePTOValue(
                    formValues?.billRate,
                    formValues?.mbpRate,
                  )
                : undefined,
            specialInstructions: undefined,
            memberBilledIndicator: payCode?.memberBilledIndicatorDefaultValue,
            hasBillRateCustomLogic: false,
            hasPayRateCustomLogic: false,
          }))
        : [],
    };
  };

  const refreshTabChildren = (updatedTabs: TabsProps['items']) => {
    return updatedTabs?.map((tab, index) => {
      if (index === 0) return tab;

      const hasSchedulerChanges = Boolean(
        predefinedFinancialSchedulerValues?.multiRates?.find(
          (multiRate) => multiRate.payTypeId === tab.key,
        ),
      );

      return {
        ...tab,
        children: (
          <MultiRateDetails
            index={index - 1}
            formNamePrefix={MULTI_RATES_KEY}
            updatePayType={modifyPayType}
            isScheduleMode={mode === MultiRatesTypeEnum.Schedule}
            assignmentType={selectedAssignmentType}
            payTypeId={tab.key || ''}
          />
        ),
        closable: !hasSchedulerChanges,
      };
    });
  };

  const findMatchingProperties = (errors: any, touched: any) => {
    const flattenErrors = flatten(errors || {});
    const flattenTouched = flatten(touched || {});

    if (typeof flattenErrors !== 'object' || flattenErrors === null) {
      return [];
    }
    return Object.keys(flattenErrors).filter((key) =>
      get(flattenTouched, key, false),
    );
  };

  const modifyTab = (tab: any, formik: FormikProps<FinancialFormValues>) => {
    if (tab.key === financialInfoTabId) {
      const { multiRates: multiRateErrors, ...otherErrors } =
        formik.errors || {};
      const { multiRates: multiRateTouched, ...otherTouched } =
        formik.touched || {};

      const matchingErrorProperties = findMatchingProperties(
        otherErrors,
        otherTouched,
      );

      return {
        ...tab,
        label: getTabLabel(
          financialInfoLabel,
          matchingErrorProperties.length > 0,
        ),
      };
    }
    const multiRatesValues = (formik.values as FinancialInfoFormValues)
      .multiRates;
    const multiRatesErrors = (formik.errors as FinancialInfoFormValues)
      .multiRates;

    const findIndex = multiRatesValues?.findIndex(
      (multiRate) => multiRate.payTypeId === tab.key,
    );

    if (isNil(findIndex)) return { ...tab };

    const errors = multiRatesErrors?.[findIndex];
    const hasTouched = (formik as any).touched.multiRates?.[findIndex];

    const matchingProperties = findMatchingProperties(errors, hasTouched);

    // checking if at least one pay code is filled or not
    const hasPayCodesInfoError = isString(errors?.payCodesInfo);
    const hasPayCodesInfoTouched = Boolean(hasTouched?.payCodesInfo);
    const isPayCodesInfoErrorAndTouched =
      hasPayCodesInfoError && hasPayCodesInfoTouched;

    const payRateTypeInfo = payRateTypes.find(
      (payRateType) => payRateType.id === tab.key,
    );

    return {
      ...tab,
      label: getTabLabel(
        payRateTypeInfo?.name,
        matchingProperties.length > 0 || isPayCodesInfoErrorAndTouched,
      ),
    };
  };

  const handleFormChange = (formik: FormikProps<FinancialFormValues>) => {
    setTabs((tabs) => tabs?.map((tab) => modifyTab(tab, formik)));
  };

  useEffect(() => {
    handleFormChange(form);
    // eslint-disable-next-line
  }, [form.values, form.touched, form.errors]);

  return [tabs, addNewTab, removeTab, activeTab, changeActiveTab] as const;
}
